/*  Blog post reader — /blog/:slug
    ────────────────────────────────────────────────────────────────
    iter100.153 — Magazine-style article page. Renders markdown via
    react-markdown + remark-gfm, layered with a hero image, author
    card, related-posts strip, and share row.
*/
import React, { useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useBlogPost } from "@/lib/useBlog";
import {
    Sparkle, Clock, ArrowRight, ArrowLeft, LinkSimple, User,
    WhatsappLogo, LinkedinLogo, TwitterLogo, Heart, Stethoscope,
    Stack, Newspaper, ShieldCheck,
} from "@phosphor-icons/react";


/* Inline meta setter — the shared SeoHead component reads from a CMS
   page slug and blog posts don't have one, so we upsert the tags
   directly here. */
function useBlogSeo({ title, description, image }) {
    useEffect(() => {
        if (title) document.title = title;
        const upsertMeta = (attr, value, content) => {
            if (!content) return;
            let el = document.head.querySelector(`meta[${attr}="${value}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, value);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        if (description) upsertMeta("name", "description", description);
        if (title) upsertMeta("property", "og:title", title);
        if (description) upsertMeta("property", "og:description", description);
        if (image) upsertMeta("property", "og:image", image);
        if (title) upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    }, [title, description, image]);
}


const CATEGORY_ICONS = {
    "smile-science":      Sparkle,
    "patient-stories":    Heart,
    "clinical-insights":  Stethoscope,
    "product-deep-dives": Stack,
    "industry-news":      Newspaper,
    "aftercare":          ShieldCheck,
};


function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
}

function categoryLabel(id) {
    const map = {
        "smile-science": "Smile Science",
        "patient-stories": "Patient Stories",
        "clinical-insights": "Clinical Insights",
        "product-deep-dives": "Product Deep-Dives",
        "industry-news": "Industry & News",
        "aftercare": "Aftercare",
    };
    return map[id] || id;
}


export default function BlogPost() {
    const { slug } = useParams();
    const { post, related, loading, error } = useBlogPost(slug);

    useBlogSeo({
        title: post ? (post.seo_title || `${post.title} · Invisique™ Blog`) : "Invisique™ Blog",
        description: post ? (post.seo_description || post.excerpt) : "",
        image: post ? (post.seo_og_image || post.hero_image_url) : "",
    });

    if (loading) {
        return (
            <div className="min-h-[60vh] grid place-items-center bg-background" data-testid="blog-post-loading">
                <div className="text-muted-foreground text-sm">Loading article…</div>
            </div>
        );
    }
    if (error === "not_found" || !post) {
        return <NotFound />;
    }

    return (
        <div data-testid="blog-post-page">
            <ArticleHero post={post} />
            <ArticleBody post={post} />
            <RelatedPosts posts={related} />
            <ClosingCta />
        </div>
    );
}


/* ================================================================
   Hero
   ================================================================ */
function ArticleHero({ post }) {
    const Icon = CATEGORY_ICONS[post.category] || Sparkle;
    return (
        <section className="relative overflow-hidden bg-background border-b border-border/60" data-testid="blog-post-hero">
            <div className="absolute inset-0 blue-grid-bg opacity-30 mask-fade-b pointer-events-none" aria-hidden="true" />
            <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 lg:pt-14 pb-10 relative">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary mb-8 transition-colors"
                    data-testid="blog-post-back-link"
                >
                    <ArrowLeft size={13} weight="bold" />
                    Back to all articles
                </Link>

                <div className="flex items-center gap-2 mb-5">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold tracking-[0.18em] uppercase">
                        <Icon size={11} weight="fill" />
                        {categoryLabel(post.category)}
                    </div>
                    {post.audience === "professional" && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/70 text-amber-700 text-[10px] font-bold tracking-[0.18em] uppercase">
                            <Stethoscope size={11} weight="fill" />
                            For Professionals
                        </div>
                    )}
                </div>

                <h1 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-foreground" data-testid="blog-post-title">
                    {post.title}
                </h1>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                    {post.excerpt}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <AuthorCard post={post} />
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-3">
                        <span className="inline-flex items-center gap-1.5">
                            <Clock size={12} weight="fill" className="text-primary" />
                            <span className="font-medium text-foreground/80">{post.reading_time_min || 1} min read</span>
                        </span>
                        <span>·</span>
                        <span>{fmtDate(post.published_at)}</span>
                    </div>
                </div>
            </div>

            {/* iter100.157w — Editorial hero: constrained to body width
                (max-w-3xl) with a shorter 21:9 cinematic crop instead of
                the previous full-bleed max-w-5xl / 16:9 which dominated
                the fold. */}
            {post.hero_image_url && (
                <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-8" data-testid="blog-post-hero-image">
                    <figure className="rounded-2xl overflow-hidden bento-shadow">
                        <img
                            src={post.hero_image_url}
                            alt={post.hero_image_alt || post.title}
                            className="w-full aspect-[21/9] object-cover"
                        />
                    </figure>
                </div>
            )}
        </section>
    );
}


function AuthorCard({ post }) {
    // iter100.157w — Author byline reads as "Invisique™ Editorial" on
    // the public reader. The real author's name/role/avatar are still
    // stored for internal attribution but no longer surfaced publicly
    // — showing an individual's role under a corporate byline reads
    // as inconsistent, so we drop the role subtitle too.
    return (
        <div className="inline-flex items-center gap-2.5" data-testid="blog-post-author">
            <div
                className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center ring-2 ring-white shadow-sm"
                aria-hidden="true"
            >
                <Sparkle size={14} weight="fill" />
            </div>
            <div className="text-sm font-semibold text-foreground leading-tight">
                Invisique&trade; Editorial
            </div>
        </div>
    );
}


/* ================================================================
   Body (markdown + share row)
   ================================================================ */
function ArticleBody({ post }) {
    const shareUrl = useMemo(() => (typeof window !== "undefined" ? window.location.href : ""), []);
    const shareText = post.title;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard");
        } catch {
            toast.error("Couldn't copy link — try selecting the URL manually.");
        }
    };

    return (
        <section className="py-12 lg:py-16 bg-background" data-testid="blog-post-body">
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <article className="prose-invisique" data-testid="blog-post-markdown">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={mdComponents}
                    >
                        {post.content_md || ""}
                    </ReactMarkdown>
                </article>

                <div className="mt-12 pt-8 border-t border-border/70">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Share this article</div>
                    <div className="flex flex-wrap gap-2">
                        <ShareButton
                            icon={WhatsappLogo}
                            label="WhatsApp"
                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                            testid="blog-share-whatsapp"
                        />
                        <ShareButton
                            icon={LinkedinLogo}
                            label="LinkedIn"
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                            testid="blog-share-linkedin"
                        />
                        <ShareButton
                            icon={TwitterLogo}
                            label="X"
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                            testid="blog-share-twitter"
                        />
                        <button
                            type="button"
                            onClick={copyLink}
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold bg-secondary/70 text-foreground/80 hover:bg-secondary hover:text-foreground transition-colors"
                            data-testid="blog-share-copy"
                        >
                            <LinkSimple size={13} weight="bold" />
                            Copy link
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}


function ShareButton({ icon: Icon, label, href, testid }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold bg-secondary/70 text-foreground/80 hover:bg-primary hover:text-primary-foreground transition-colors"
        >
            <Icon size={13} weight="fill" />
            {label}
        </a>
    );
}


/* Markdown component overrides for in-brand typography. Uses no
   external CSS classes beyond utility Tailwind — every element gets
   the correct rhythm. */
const mdComponents = {
    h2: ({ node, ...props }) => (
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-12 mb-4 text-foreground" {...props} />
    ),
    h3: ({ node, ...props }) => (
        <h3 className="font-display text-xl font-bold tracking-tight mt-10 mb-3 text-foreground" {...props} />
    ),
    p: ({ node, ...props }) => (
        <p className="text-base text-foreground/85 leading-[1.75] my-5" {...props} />
    ),
    ul: ({ node, ...props }) => (
        <ul className="list-disc pl-6 my-5 space-y-2 text-foreground/85 leading-relaxed marker:text-primary" {...props} />
    ),
    ol: ({ node, ...props }) => (
        <ol className="list-decimal pl-6 my-5 space-y-2 text-foreground/85 leading-relaxed marker:text-primary marker:font-semibold" {...props} />
    ),
    li: ({ node, ...props }) => (
        <li className="pl-1 leading-relaxed" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
        <blockquote
            className="my-8 pl-6 border-l-4 border-primary/40 py-2 italic text-foreground/90 font-display text-lg leading-relaxed"
            {...props}
        />
    ),
    a: ({ node, ...props }) => (
        <a className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors" {...props} />
    ),
    strong: ({ node, ...props }) => (
        <strong className="font-semibold text-foreground" {...props} />
    ),
    code: ({ inline, ...props }) => (
        inline
            ? <code className="px-1.5 py-0.5 rounded bg-secondary/60 text-primary font-mono text-[0.9em]" {...props} />
            : <code className="block p-4 rounded-lg bg-secondary/60 text-foreground font-mono text-sm overflow-x-auto" {...props} />
    ),
    hr: ({ node, ...props }) => (
        <hr className="my-10 border-t-2 border-dashed border-border/60" {...props} />
    ),
    img: ({ node, ...props }) => (
        <img className="rounded-2xl my-8 w-full" loading="lazy" alt={props.alt || ""} {...props} />
    ),
};


/* ================================================================
   Related posts
   ================================================================ */
function RelatedPosts({ posts }) {
    if (!posts || posts.length === 0) return null;
    return (
        <section className="py-14 lg:py-20 bg-secondary/30 border-t border-border/60" data-testid="blog-post-related">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="overline text-primary mb-3">Keep reading</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">
                    More in this series
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                    {posts.map((p) => {
                        const Icon = CATEGORY_ICONS[p.category] || Sparkle;
                        return (
                            <Link
                                key={p.id}
                                to={`/blog/${p.slug}`}
                                className="group block rounded-2xl overflow-hidden border border-border/70 bg-card hover:border-primary/30 transition-all bento-shadow"
                                data-testid={`blog-related-${p.slug}`}
                            >
                                <div className="aspect-[4/3] bg-secondary overflow-hidden">
                                    {p.hero_image_url ? (
                                        <img
                                            src={p.hero_image_url}
                                            alt={p.hero_image_alt || p.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full grid place-items-center">
                                            <Icon size={40} weight="duotone" className="text-primary/30" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-2">
                                        <Icon size={11} weight="fill" />
                                        {categoryLabel(p.category)}
                                    </div>
                                    <h3 className="font-display text-base font-bold leading-snug group-hover:text-primary transition-colors line-clamp-2">
                                        {p.title}
                                    </h3>
                                    <div className="mt-3 text-[11px] text-muted-foreground">
                                        {p.reading_time_min || 1} min · {fmtDate(p.published_at)}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Closing CTA
   ================================================================ */
function ClosingCta() {
    return (
        <section className="py-14 lg:py-20 bg-background border-t border-border/60" data-testid="blog-post-cta">
            <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[10px] font-bold tracking-[0.22em] uppercase">
                    <Sparkle size={11} weight="fill" /> Ready to start?
                </div>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-[2rem] font-bold tracking-tight leading-tight max-w-2xl mx-auto">
                    See where your case sits.{" "}
                    <span className="text-primary">A written quote, no gimmicks.</span>
                </h2>
                <p className="mt-3 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                    Book a free smile assessment with a certified Invisique™ orthodontist. Sixty seconds to schedule.
                </p>
                <div className="mt-8">
                    <Link to="/smile-assessment" data-testid="blog-post-cta-link">
                        <Button className="h-12 px-6 rounded-full bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:-translate-y-0.5 transition-all">
                            <Sparkle size={14} weight="fill" className="mr-2 text-amber-300" />
                            Free Smile Assessment
                            <ArrowRight size={14} weight="bold" className="ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
}


function NotFound() {
    return (
        <div className="min-h-[60vh] grid place-items-center bg-background px-5" data-testid="blog-post-404">
            <div className="text-center max-w-md">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-3">404</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold">This article isn&apos;t here.</h1>
                <p className="text-muted-foreground mt-3">
                    It may have been unpublished, moved, or the link may be broken.
                </p>
                <Link
                    to="/blog"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                >
                    Back to all articles
                    <ArrowRight size={13} weight="bold" />
                </Link>
            </div>
        </div>
    );
}
